import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../../components/DataTable/DataTable';
import { IColInfo } from '../../../components/DataTable/Interfaces';
import SwitchField from '../../../components/Fields/SwitchField';
import { FormConfig } from '../../../components/Fields/Types';
import useSelectListOptions from '../../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../../components/Hooks/ValidationHook/useValidation';
import Popup from '../../../components/Popups/Popup';
import SpinnerOverlay from "../../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../../contexts/authContext';
import { useNotifications } from '../../../contexts/notificationContext';
import { useTranslations } from '../../../contexts/translationContext';
import TranslationModel from '../../../models/TranslationModel';
import DataService from '../../../services/data.service';
import { PopupProps } from '../../../types/PopupProps';
import { Result } from '../../../types/Result';
import Button from '../../../components/Buttons/Button';
import ButtonBar from '../../../components/Fields/ButtonBar';
import SelectListField from '../../../components/Fields/SelectListField';
import TextboxField from '../../../components/Fields/TextboxField';
import Unauthorized from '../../Authentication/Unauthorized';

interface TranslationSearchCriteria {
    translationKey?: string
    translatedText?: string
    language?: string
    context?: string
    unverifiedOnly: boolean
    unapprovedOnly: boolean
    unfinalizedOnly: boolean
}

const TranslationsSearchPopup: React.FC<PopupProps> = ({show, onHide }) => {
    const [tableData, setTableData] = useState<TranslationModel[]>([])
    const [showCreatePopup, setShowCreatePopup] = useState<boolean>(false);
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const { handleResult, handleException } = useNotifications();
    const { isAuthorizedFor, user } = useAuth()
    const { getContext } = useTranslations()
    const navigate = useNavigate()
    const [formData, setFormData] = useState<TranslationSearchCriteria>({
        language: user?.language,
        context: getContext(),
        unverifiedOnly: false,
        unapprovedOnly: false,
        unfinalizedOnly: false,
    });

    var context = getContext();
    console.log('context', context);

    useEffect(() => {
        setFormData(x => { return { ...x, context: context } })
        search()
    }, [context])

    const languages = useSelectListOptions('api/v1/selectlist/Languages', '-- All languages');
    const contexts = useSelectListOptions('api/v1/selectlist/Contexts', '-- All contexts');

    function search() {
        setShowSpinner(true);
        DataService
            .post<TranslationModel[]>(`api/v1/Translations/Search/`, formData)
            .then(response => {
                setShowSpinner(false);
                setTableData(response);
            })
            .catch(e => {
                setShowSpinner(false);
                handleException(e)
            });
    }

    function verifyRow(rowData) {
        DataService.post<Result>(`api/v1/Translations/${rowData.id}/Verify`, rowData)
            .then(response => {
                handleResult(response);
                search();
            })
            .catch(e => { handleException(e); });
    }

    function approveRow(rowData) {
        DataService.post<Result>(`api/v1/Translations/${rowData.id}/Approve`, rowData)
            .then(response => {
                handleResult(response);
                search();
            })
            .catch(e => { handleException(e); });
    }

    function finalizeRow(rowData) {
        DataService.post<Result>(`api/v1/Translations/${rowData.id}/Finalize`, rowData)
            .then(response => {
                handleResult(response);
                search();
            })
            .catch(e => { handleException(e); });
    }
    function deleteRow(rowData) {
        DataService.delete('api/v1/Translations/', rowData.id)
            .then(response => {
                handleResult(response);
                search();
            })
            .catch(e => { handleException(e); });
    }


    function onFieldChanged(name: string, value: string): void {
        formData[name] = value;
        setFormData(formData);

        console.log(name + ': ' + value);
    }

    function onCellChange(value: any, rowIndex: number, dataProp?: string) {
        console.log('cell change', value, rowIndex, dataProp)
        console.log('tableData', tableData)
        if (tableData && dataProp) {
            dataProp.split('.').reduce((object, propertyName, index, elements) => {
                if (index === elements.length - 1) {
                    object[propertyName] = value;
                    setTableData(tableData.slice());
                }

                if (object && object[propertyName]) {
                    return object[propertyName];
                }
                return null;
            }, tableData[rowIndex]);
        }
    }

    function getStatusColor(rowData: TranslationModel, colInfo: IColInfo): string | undefined {
        let color = "#ddd";
        if (rowData.verifiedOn !== undefined) { color = "green"; }
        if (rowData.approvedOn !== undefined) { color = "#145A32"; }
        if (rowData.finalizedOn !== undefined) { color = "Black"; }
        if (rowData.deletedOn !== undefined) { color = "Red"; }

        return color
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'translationKey', label: 'Translation Key', labelSize: 2 },
            { name: 'translatedText', label: 'Translated Text', labelSize: 2 },
            { name: 'language', label: 'Language', labelSize: 2 },
            { name: 'context', label: 'Context', labelSize: 2 },
            { name: 'unverifiedOnly', label: 'Show unverified translations only', labelSize: 5 },
            { name: 'unapprovedOnly', label: 'Show unapproved translations only', labelSize: 5 },
            { name: 'unfinalizedOnly', label: 'Show unfinalized translations only', labelSize: 5 },
        ],
        handleSubmit: search,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);


    if (!isAuthorizedFor(ApplicationFunctions.ViewTranslations)) {
        return (<Unauthorized />);
    }

    console.log('formData', formData);

    return (
        <Popup show={show} onHide={onHide} size={'xxl'} title={'Translations'} >
            <Popup.Body>
                <Container fluid>
                    <Row>
                        <Col md='6'>
                            <TextboxField {...getFieldProps('translationKey')} />
                            <TextboxField {...getFieldProps('translatedText')} />
                            <SelectListField {...getFieldProps('language')} options={languages} />
                            <SelectListField {...getFieldProps('context')} options={contexts} />
                        </Col>
                        <Col md='6'>
                            <SwitchField {...getFieldProps('unverifiedOnly')} />
                            <SwitchField {...getFieldProps('unapprovedOnly')} />
                            <SwitchField {...getFieldProps('unfinalizedOnly')} />
                        </Col>
                    </Row>
                </Container>
                <ButtonBar offset={2}>
                    <Button text='Search' {...getSubmitButtonProps()} />
                </ButtonBar>
                <div className="card" style={{ margin: 12 }}>
                    <DataTable
                        name='dtTranslations'
                        tableData={tableData}
                        onCellChange={onCellChange}
                        colInfos={[
                            { key: true, dataProp: 'id', visible: false },
                            { getCellBackgroundColor: getStatusColor, sort: false, width: 5 },
                            { title: 'Language', dataProp: 'language' },
                            { title: 'Context', dataProp: 'context' },
                            { title: 'Translation key', dataProp: 'translationKey' },
                            { title: 'Translated text', dataProp: 'translatedText', width: 250, editable: true },
                            { title: 'Used on', dataProp: 'lastAccessedOn', dataType: 'datetime' },
                            {
                                title: 'Verify',
                                sort: false,
                                rowButtons: [{ text: "Verify", icon: 'fa-check', color: 'green', onClick: verifyRow, applicationFunctions: [ApplicationFunctions.VerifyTranslation] }],
                            },
                            {
                                title: 'Approve',
                                sort: false,
                                rowButtons: [{ text: "Approve", icon: 'fa-thumbs-up', color: '#145A32', onClick: approveRow, applicationFunctions: [ApplicationFunctions.ApproveTranslation] }],
                            },
                            {
                                title: 'Finalize',
                                sort: false,
                                rowButtons: [{ text: "Finalize", icon: 'fa-gift', color: 'black', onClick: finalizeRow, applicationFunctions: [ApplicationFunctions.ApproveTranslation] }],
                            },
                            {
                                title: 'Delete',
                                sort: false,
                                rowButtons: [{ text: "Delete", icon: 'fa-trash', color: '#F3565D', onClick: deleteRow, applicationFunctions: [ApplicationFunctions.ApproveTranslation] }],
                            },
                        ]}
                    />
                </div>
            </Popup.Body>
            {showSpinner && (<SpinnerOverlay />)}
        </Popup>
    );
}
export default TranslationsSearchPopup;